import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useMemo, } from 'react';
import { TransitionGroup } from 'react-transition-group';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { Fade } from '../Fade/index';
import { placements } from './constants';
const arrowWidth = 14;
const arrowHeight = 8;
const arrowOffset = arrowWidth / 2;
const spacingOffset = arrowOffset + 4;
const arrowVariationStyles = {
    bottom: css `
    border-bottom-color: ${theme.colors.gray700};
    border-bottom-width: ${styleUtils.rem(arrowHeight)};
    border-top: none;
    right: calc(50% - ${styleUtils.rem(arrowOffset)});
    top: -${styleUtils.rem(arrowOffset)};
  `,
    left: css `
    border-left-color: ${theme.colors.gray700};
    border-left-width: ${styleUtils.rem(arrowHeight)};
    border-right: none;
    bottom: calc(50% - ${styleUtils.rem(arrowOffset)});
    right: -${styleUtils.rem(arrowOffset)};
  `,
    right: css `
    border-left: none;
    border-right-color: ${theme.colors.gray700};
    border-right-width: ${styleUtils.rem(arrowHeight)};
    bottom: calc(50% - ${styleUtils.rem(arrowOffset)});
    left: -${styleUtils.rem(arrowOffset)};
  `,
    top: css `
    border-bottom: none;
    border-top-color: ${theme.colors.gray700};
    border-top-width: ${styleUtils.rem(arrowHeight)};
    bottom: -${styleUtils.rem(arrowOffset)};
    right: calc(50% - ${styleUtils.rem(arrowOffset)});
  `,
};
const verticalPadding = (styleUtils.remToPxValue(theme.spacing.x3) - (styleUtils.remToPxValue(theme.spacing.base) / 2));
const tooltipPlacementStyles = {
    bottom: css `
    right: 50%;
    top: calc(100% + ${styleUtils.rem(spacingOffset)});
    transform: translateX(50%);
  `,
    left: css `
    right: calc(100% + ${styleUtils.rem(spacingOffset)});
    top: 50%;
    transform: translateY(-50%);
  `,
    right: css `
    left: calc(100% + ${styleUtils.rem(spacingOffset)});
    top: 50%;
    transform: translateY(-50%);
  `,
    top: css `
    bottom: calc(100% + ${styleUtils.rem(spacingOffset)});
    right: 50%;
    transform: translateX(50%);
  `,
};
const defaultProps = {
    enterDelay: false,
    exitDelay: false,
    placement: placements.bottom,
    showTooltip: false,
};
const Tooltip = ({ children, enterDelay = defaultProps.enterDelay, exitDelay = defaultProps.exitDelay, minWidth, placement = defaultProps.placement, showTooltip = defaultProps.showTooltip, }) => {
    const tooltipArrowStyles = useMemo(() => (css `
        border-color: transparent;
        border-style: solid;
        border-width: ${styleUtils.rem(arrowOffset)};
        height: 0;
        margin: auto;
        position: absolute;
        width: 0;
        ${arrowVariationStyles[placement]}
      `), [
        placement,
    ]);
    const tooltipStyles = useMemo(() => (css `
        background-color: ${theme.colors.gray700};
        border-radius: ${theme.borderRadiusDefault};
        display: block;
        ${minWidth && css `
          min-width: ${styleUtils.rem(minWidth)};
        `}
      `), [
        minWidth,
    ]);
    const tooltipBodyStyles = useMemo(() => (css `
        ${theme.typeStyles.bodyExtraSmallStyles}
        border-radius: ${theme.borderRadiusDefault};
        box-sizing: border-box;
        color: ${theme.colors.gray100};
        padding-bottom: ${styleUtils.rem(verticalPadding)};
        padding-left: ${theme.spacing.x3};
        padding-right: ${theme.spacing.x3};
        padding-top: ${styleUtils.rem(verticalPadding)};
        white-space: nowrap;
        ${minWidth && css `
          white-space: normal;
        `}
      `), [
        minWidth,
    ]);
    const tooltipContainerStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        ${tooltipPlacementStyles[placement]}
        border-radius: ${theme.borderRadiusDefault};
        position: absolute;
        z-index: 1071;
      `), [
        placement,
    ]);
    return (_jsx(TransitionGroup, { children: showTooltip
            && (_jsx(Fade, Object.assign({ enterDelay: enterDelay, exitDelay: exitDelay }, { children: _jsxs("div", Object.assign({ css: tooltipContainerStyles }, { children: [_jsx("div", { css: tooltipArrowStyles }), _jsx("div", Object.assign({ css: tooltipStyles }, { children: _jsx("div", Object.assign({ css: tooltipBodyStyles }, { children: children })) }))] })) }))) }));
};
const MemoizedTooltip = memo(Tooltip);
export { MemoizedTooltip as Tooltip };
// MC-1068 Temporary hack for Storybook.
export default MemoizedTooltip;
